<script setup>
// 显式 import 功能组件:<component :is> 用字符串名不会触发 Nuxt 自动导入(打包时不收录→渲染空)。
import GeneratorTool from "./GeneratorTool.vue";
import StudioTool from "./StudioTool.vue";
import VideoTool from "./VideoTool.vue";
import AudioTool from "./AudioTool.vue";
import ColorTool from "./ColorTool.vue";
import EncodeTool from "./EncodeTool.vue";
import ContrastChecker from "./ContrastChecker.vue";
import ExifTool from "./ExifTool.vue";
import CollageTool from "./CollageTool.vue";
import CropTool from "./CropTool.vue";
import ResizeTool from "./ResizeTool.vue";
import FlipTool from "./FlipTool.vue";
import RotateTool from "./RotateTool.vue";
import PaywallModal from "@/components/common/PaywallModal.vue";

const props = defineProps({
  seo: { type: Object, required: true },
});

const route = useRoute();

// #19 刺绣付费墙：付费目标格式时弹窗拦截，View Plans 跳套餐页。
// PLANS_ROUTE：Pricing 导航（FotoHeaderNew/FotoHeader）+ footer 均指向 /pricing/，页面在 app/pages/pricing/index.vue。
const PLANS_ROUTE = "/pricing/";
const { isPaywallOpen, paywallFormats, closePaywall } = usePaywallModal();
function onViewPlans() { closePaywall(); navigateTo(PLANS_ROUTE); }

// #6 phase 2：颜色值计算器（RGB↔CMYK / Grayscale→RGB）走 ColorValueCalc，其余走文件转换器。
const CALC = { "RGB to CMYK Converter": "rgb2cmyk", "CMYK to RGB Converter": "cmyk2rgb", "Grayscale to RGB Converter": "gray2rgb" };
const calcMode = computed(() => CALC[(props.seo && props.seo.name) || ""] || "");
// #7：AI 引流落地页（去背景 / 增强-放大）走 AiLandingTool（上传→浏览器预览→CTA 跳主 AI 页）。
const aiMode = computed(() => {
  const n = (props.seo && props.seo.name) || "";
  if (/Background Remover/i.test(n)) return "bg";
  if (/Image Enhancer|Upscaler/i.test(n)) return "enhance";
  return "";
});
const aiSlug = computed(() => {
  const parts = (route.path || "").split("/").filter(Boolean);
  return (parts[parts.length - 1] || "").toLowerCase();
});
// #19 P3：真功能工具分发表（slug → [组件名, mode]）。功能组件只换操作区,下方 SEO 区块照常复用。
// ToolStagePlaceholder 仅作转换器兜底,避免真功能工具落到错误拖放区。逐波移植,在此登记。
const FUNC = {
  // generator
  "qr-code-generator": [GeneratorTool, "qr"],
  "barcode-generator": [GeneratorTool, "barcode"],
  // studio（canvas 图片编辑）
  "add-watermark-to-image": [StudioTool, "watermark"],
  "add-border-to-image": [StudioTool, "border"],
  "round-image-corners": [StudioTool, "corners"],
  "meme-generator": [StudioTool, "meme"],
  "black-and-white-image": [StudioTool, "bw"],
  "brightness-and-contrast": [StudioTool, "adjust"],
  "image-to-grayscale": [StudioTool, "grayscale"],
  // video（10）
  "trim-video": [VideoTool, "trim-video"],
  "crop-video": [VideoTool, "crop-video"],
  "resize-video": [VideoTool, "resize-video"],
  "change-video-speed": [VideoTool, "change-video-speed"],
  "reverse-video": [VideoTool, "reverse-video"],
  "mute-video": [VideoTool, "mute-video"],
  "merge-videos": [VideoTool, "merge-videos"],
  "extract-video-frames": [VideoTool, "extract-video-frames"],
  "add-subtitles-to-video": [VideoTool, "add-subtitles-to-video"],
  "gif-maker": [VideoTool, "gif-maker"],
  // audio（6）
  "audio-trimmer": [AudioTool, "trim"],
  "ringtone-maker": [AudioTool, "ringtone"],
  "audio-fade": [AudioTool, "fade"],
  "change-audio-speed": [AudioTool, "speed"],
  "merge-audio": [AudioTool, "merge"],
  "voice-recorder": [AudioTool, "record"],
  // color
  "color-palette-generator": [ColorTool, "palette"],
  "gradient-generator": [ColorTool, "gradient"],
  "color-picker": [ColorTool, "picker"],
  // encode（路由落 /converter/ 但末段 slug 一致）
  "image-to-ascii": [EncodeTool, "ascii"],
  "image-to-base64": [EncodeTool, "base64"],
  // contrast
  "contrast-checker": [ContrastChecker, "contrast-checker"],
  // exif
  "exif-viewer": [ExifTool, "viewer"],
  "exif-remover": [ExifTool, "remover"],
  // collage
  "photo-collage": [CollageTool, "grid"],
  // crop:hub 链接落 /image/crop（slug "crop"）,redirect/sitemap 规范用 "crop-image",两个都收
  "crop": [CropTool, "crop"],
  "crop-image": [CropTool, "crop"],
  // 变换:新设计组件,描述性 slug（旧 UCard 页 resize.vue/flipper.vue/rotator.vue 退役,见 _design-sync P4①A）
  "resize-image": [ResizeTool, "resize-image"],
  "flip-image": [FlipTool, "flip-image"],
  "rotate-image": [RotateTool, "rotate-image"],
};
const funcTool = computed(() => FUNC[aiSlug.value] || null);
// #19 ①B：根转换器/压缩器（"Image Converter"/"Image Compressor"）走新设计 ToolStagePlaceholder 的 root 模式。
const rootKind = computed(() => {
  const n = ((props.seo && props.seo.name) || "").toLowerCase().trim();
  if (/^(image|video|audio) converter$/.test(n)) return "converter";
  if (/^(image|video|audio) compressor$/.test(n)) return "compressor";
  return "";
});
// 广告重载 key（来自 ToolStagePlaceholder 的 stage-change）：仅在上传 / 转换完成 / 重置时变化，
// 改参数不变。变化时给左(sky)、右(FxRail)、底(leaderboard)三块换 key 重挂 → 各重载一次。
const toolStage = ref("drop");
</script>

<template>
  <div class="ft-scope">
    <!-- 1. 面包屑 -->
    <div class="wrap">
      <ToolBreadcrumb :items="seo.crumb" />
    </div>

    <!-- 2. Hero(explainer 用 heroSub 提问式副标题 + plain:不拼推销尾巴/不显免费徽标,避免和下方 verdict 重复) -->
    <ToolHero :name="seo.name" :desc="seo.explainer ? (seo.heroSub || seo.desc) : seo.desc" :plain="!!seo.explainer" />

    <!-- 3. 主转换区：三栏（左广告位 | 中转换器+信任+横幅广告 | 右 AI 效果引流） -->
    <div class="wrap wrap-wide">
      <div class="tool-layout">
        <aside class="ad-rail">
          <AdSlot variant="sky" :reload-key="'sky:' + route.path + ':' + toolStage" />
        </aside>

        <div class="tool-col">
          <!-- 不可能/不可写/不可读的配对:动作区不放上传控件,放诚实解释(为什么做不到 + 能做什么)。外壳其余(广告/推荐/FAQ)照常。 -->
          <RawTargetExplainer v-if="seo.explainer" :seo="seo" />
          <component :is="funcTool[0]" v-else-if="funcTool" :mode="funcTool[1]" :slug="aiSlug" />
          <ColorValueCalc v-else-if="calcMode" :mode="calcMode" />
          <AiLandingTool v-else-if="aiMode" :mode="aiMode" :slug="aiSlug" />
          <ToolStagePlaceholder v-else :name="seo.name" :root="rootKind" @stage-change="toolStage = $event" />
          <TrustBadges />
          <AdSlot variant="leaderboard" :reload-key="'lead:' + route.path + ':' + toolStage" />
        </div>

        <aside class="ad-rail">
          <FxRail :key="'fx:' + route.path + ':' + toolStage" />
        </aside>
      </div>
    </div>

    <!-- 4-14. 各组件自带 <section>/.wrap，FreeToolPage 不再包裹，仅按 design 注入顺序排列 -->

    <!-- 4. How it works（静态步骤带） -->
    <HowItWorks v-if="seo.steps && seo.steps.length" :title="seo.stepsTitle" :steps="seo.steps" />

    <!-- 5. Related free tools（交叉引流卡片带） -->
    <RelatedToolCards v-if="seo.cloud && seo.cloud.length" :tools="seo.cloud" />

    <!-- 6. AI Effects 引流带（替换 design 的静态 upsell） -->
    <UpsellSection />

    <!-- 7. Why use this free tool -->
    <SeoWhyGrid v-if="seo.why && seo.why.length" :title="seo.whyTitle" :items="seo.why" />

    <!-- 8. 正文（intro prose） -->
    <SeoProse v-if="seo.intro && seo.intro.length" :blocks="seo.intro" />

    <!-- 9. 对比表（可选） -->
    <SeoCompareTable v-if="seo.compare" :compare="seo.compare" :title="seo.compareTitle" />

    <!-- 10. 格式详情（可选） -->
    <SeoFormatDetails v-if="seo.formatBlocks" :blocks="seo.formatBlocks" />

    <!-- 11. 转换标签云分组（可选） -->
    <SeoConvertClouds v-if="seo.convertClouds" :groups="seo.convertClouds" />

    <!-- 12. FAQ -->
    <SeoFaq v-if="seo.faq && seo.faq.length" :items="seo.faq" />

    <!-- 13. 评价 -->
    <SeoReviews v-if="seo.reviews && seo.reviews.length" :rating="seo.rating" :reviews="seo.reviews" />

    <!-- 14. 热门工具标签云 -->
    <SeoCloud v-if="seo.cloud && seo.cloud.length" :title="seo.cloudTitle" :links="seo.cloud" />

    <!-- 刺绣付费墙：付费目标格式时由 usePaywallModal 控制弹出 -->
    <PaywallModal :open="isPaywallOpen" :formats="paywallFormats" @close="closePaywall" @view-plans="onViewPlans" />
  </div>
</template>
